import { authenticatedFetchRaw } from '../services/api';
import { LOGO_SVG } from '../components/icons/Logo';

export const MAX_LOGO_SIZE_BYTES = 2 * 1024 * 1024;
export const ACCEPTED_LOGO_TYPES = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/webp'];
export const LOGO_ACCEPT_ATTRIBUTE = '.svg,.png,.jpg,.jpeg,.webp,image/svg+xml,image/png,image/jpeg,image/webp';

const readFileAsText = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('Impossibile leggere il file SVG'));
  reader.readAsText(file);
});

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('Impossibile leggere il file immagine'));
  reader.readAsDataURL(file);
});

const loadImageDimensions = (sourceUrl) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve({
    width: image.naturalWidth || image.width || null,
    height: image.naturalHeight || image.height || null
  });
  image.onerror = () => reject(new Error('Impossibile leggere le dimensioni del logo'));
  image.src = sourceUrl;
});

const inferMimeType = (file) => {
  if (file.type) return file.type;

  const extension = file.name.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'svg':
      return 'image/svg+xml';
    case 'png':
      return 'image/png';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'webp':
      return 'image/webp';
    default:
      return '';
  }
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(reader.error || new Error('Impossibile convertire il logo'));
  reader.readAsDataURL(blob);
});

const parseSvgDimensions = (svgText) => {
  try {
    const document = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svgElement = document.documentElement;
    if (!svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
      return { width: null, height: null };
    }

    const width = Number.parseFloat(svgElement.getAttribute('width'));
    const height = Number.parseFloat(svgElement.getAttribute('height'));
    if (Number.isFinite(width) && Number.isFinite(height)) {
      return { width, height };
    }

    const viewBox = svgElement.getAttribute('viewBox');
    if (!viewBox) {
      return { width: null, height: null };
    }

    const parts = viewBox.trim().split(/[\s,]+/).map(Number);
    if (parts.length === 4 && Number.isFinite(parts[2]) && Number.isFinite(parts[3])) {
      return { width: parts[2], height: parts[3] };
    }
  } catch (error) {
    return { width: null, height: null };
  }

  return { width: null, height: null };
};

const parseInlineSvgStyles = (cssText) => {
  const classRules = new Map();
  const ruleRegex = /\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/g;
  let match;

  while ((match = ruleRegex.exec(cssText)) !== null) {
    const [, className, declarations] = match;
    const attrs = {};

    declarations
      .split(';')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .forEach((entry) => {
        const [rawProperty, rawValue] = entry.split(':');
        if (!rawProperty || !rawValue) return;

        const property = rawProperty.trim().toLowerCase();
        const value = rawValue.trim();

        switch (property) {
          case 'fill':
            attrs.fill = value;
            break;
          case 'stroke':
            attrs.stroke = value;
            break;
          case 'stroke-width':
            attrs['stroke-width'] = value;
            break;
          case 'opacity':
            attrs.opacity = value;
            break;
          case 'fill-opacity':
            attrs['fill-opacity'] = value;
            break;
          case 'stroke-opacity':
            attrs['stroke-opacity'] = value;
            break;
          default:
            break;
        }
      });

    if (Object.keys(attrs).length > 0) {
      classRules.set(className, attrs);
    }
  }

  return classRules;
};

export const normalizeSvgForPdf = (svgText) => {
  try {
    const document = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    const svgElement = document.documentElement;
    if (!svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
      return svgText;
    }

    const parserError = document.querySelector('parsererror');
    if (parserError) {
      return svgText;
    }

    if (!svgElement.getAttribute('viewBox')) {
      const width = Number.parseFloat(svgElement.getAttribute('width'));
      const height = Number.parseFloat(svgElement.getAttribute('height'));
      if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
        svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
      }
    }

    const styleNodes = Array.from(document.querySelectorAll('style'));
    const mergedClassRules = new Map();

    styleNodes.forEach((styleNode) => {
      const rules = parseInlineSvgStyles(styleNode.textContent || '');
      rules.forEach((attrs, className) => {
        mergedClassRules.set(className, {
          ...(mergedClassRules.get(className) || {}),
          ...attrs
        });
      });
    });

    if (mergedClassRules.size > 0) {
      document.querySelectorAll('[class]').forEach((element) => {
        const classNames = (element.getAttribute('class') || '')
          .split(/\s+/)
          .map((entry) => entry.trim())
          .filter(Boolean);

        classNames.forEach((className) => {
          const attrs = mergedClassRules.get(className);
          if (!attrs) return;

          Object.entries(attrs).forEach(([attrName, attrValue]) => {
            if (!element.hasAttribute(attrName)) {
              element.setAttribute(attrName, attrValue);
            }
          });
        });
      });

      styleNodes.forEach((styleNode) => styleNode.remove());
    }

    svgElement.removeAttribute('width');
    svgElement.removeAttribute('height');

    return new XMLSerializer().serializeToString(svgElement);
  } catch (error) {
    return svgText;
  }
};

export const prepareLogoUploadPayload = async (file) => {
  if (!file) {
    throw new Error('Nessun file selezionato');
  }

  if (file.size <= 0) {
    throw new Error('Il file logo e vuoto');
  }

  if (file.size > MAX_LOGO_SIZE_BYTES) {
    throw new Error(`Il logo supera il limite di ${Math.round(MAX_LOGO_SIZE_BYTES / 1024 / 1024)} MB`);
  }

  const mimeType = inferMimeType(file);
  if (!ACCEPTED_LOGO_TYPES.includes(mimeType)) {
    throw new Error('Formato logo non supportato. Usa SVG, PNG, JPG o WEBP.');
  }

  if (mimeType === 'image/svg+xml') {
    const content = await readFileAsText(file);
    const { width, height } = parseSvgDimensions(content);
    return {
      fileName: file.name,
      mimeType,
      content,
      width,
      height
    };
  }

  const content = await readFileAsDataUrl(file);
  const objectUrl = URL.createObjectURL(file);

  try {
    const { width, height } = await loadImageDimensions(objectUrl);
    return {
      fileName: file.name,
      mimeType,
      content,
      width,
      height
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

export const loadCurrentUserLogoPreviewUrl = async () => {
  const response = await authenticatedFetchRaw('/settings/logo/current');
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

export const formatLogoTimestamp = (timestamp, locale = 'it-IT') => {
  if (!timestamp) return '—';

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '—';

  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

export const getPdfImageFormat = (mimeType = '') => {
  switch (mimeType) {
    case 'image/png':
      return 'PNG';
    case 'image/webp':
      return 'WEBP';
    case 'image/jpeg':
    default:
      return 'JPEG';
  }
};

export const calculateLogoRenderSize = (sourceWidth, sourceHeight, maxWidth, maxHeight) => {
  if (!sourceWidth || !sourceHeight) {
    return { width: maxWidth, height: maxHeight };
  }

  const ratio = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
  return {
    width: Math.max(1, sourceWidth * ratio),
    height: Math.max(1, sourceHeight * ratio)
  };
};

export const resolvePdfLogoAsset = async (settings) => {
  const logoMetadata = settings?.profile_data?.logo;

  if (!logoMetadata?.relativePath) {
    return {
      kind: 'svg',
      mimeType: 'image/svg+xml',
      content: LOGO_SVG,
      width: 1,
      height: 1,
      isFallback: true
    };
  }

  try {
    const response = await authenticatedFetchRaw('/settings/logo/current');
    const blob = await response.blob();
    const mimeType = blob.type || logoMetadata.mimeType;

    if (mimeType === 'image/svg+xml') {
      const content = await blob.text();
      const normalizedContent = normalizeSvgForPdf(content);
      const parsedDimensions = parseSvgDimensions(normalizedContent);
      return {
        kind: 'svg',
        mimeType,
        content: normalizedContent,
        width: logoMetadata.width || parsedDimensions.width || 1,
        height: logoMetadata.height || parsedDimensions.height || 1,
        isFallback: false
      };
    }

    return {
      kind: 'raster',
      mimeType,
      content: await blobToDataUrl(blob),
      width: logoMetadata.width || null,
      height: logoMetadata.height || null,
      isFallback: false
    };
  } catch (error) {
    return {
      kind: 'svg',
      mimeType: 'image/svg+xml',
      content: LOGO_SVG,
      width: 1,
      height: 1,
      isFallback: true
    };
  }
};

export const rasterizeSvgToPngDataUrl = async (svgText, width, height) => {
  const blob = new Blob([svgText], { type: 'image/svg+xml' });
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Impossibile rasterizzare il logo SVG'));
      img.src = objectUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width || image.naturalWidth || 256));
    canvas.height = Math.max(1, Math.round(height || image.naturalHeight || 256));

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas non disponibile per la conversione del logo');
    }

    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};
